import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Input,
  Label,
  Row,
  Col,
  Spinner,
  Alert,
  Card,
  CardBody,
  CardHeader,
} from "reactstrap";
import { useFormik } from "formik";
import * as Yup from "yup";
import {
  startMarketMaker,
  fetchEvents,
  fetchEventById,
} from "../../services/apiServices";
import { useLocation, useNavigate } from "react-router-dom";

const MarketMakerForm = ({ onSuccess }) => {
  const location = useLocation();
  const navigate = useNavigate();
  const [events, setEvents] = useState([]);
  const [markets, setMarkets] = useState([]);
  const [loadingEvents, setLoadingEvents] = useState(true);
  const [loadingMarkets, setLoadingMarkets] = useState(false);
  const [submitStatus, setSubmitStatus] = useState({
    loading: false,
    error: null,
    success: false,
  });

  // Extract event_id and market_id from URL query parameters if available
  const queryParams = new URLSearchParams(location.search);
  const eventIdFromQuery = queryParams.get("event_id");
  const marketIdFromQuery = queryParams.get("market_id");

  // Validation schema
  const validationSchema = Yup.object({
    event_id: Yup.string().required("Event selection is required"),
    market_id: Yup.string(), // Made optional - no .required()
    price_range_start: Yup.number()
      .min(1, "Price range start must be at least 1")
      .max(99, "Price range start must be less than 100")
      .required("Price range start is required"),
    price_range_end: Yup.number()
      .min(2, "Price range end must be at least 2")
      .max(100, "Price range end must be at most 100")
      .moreThan(
        Yup.ref("price_range_start"),
        "Price range end must be greater than start"
      )
      .required("Price range end is required"),
    duration_hours: Yup.number()
      .min(1, "Duration must be at least 1 hour")
      .required("Duration is required"),
    trade_frequency_minutes: Yup.number()
      .min(1, "Trade frequency must be at least 1 minute")
      .required("Trade frequency is required"),
    volume_target: Yup.number()
      .min(1, "Volume target must be at least 1")
      .required("Volume target is required"),
  });

  // Formik setup
  const formik = useFormik({
    initialValues: {
      event_id: eventIdFromQuery || "",
      market_id: marketIdFromQuery || "",
      price_range_start: 30,
      price_range_end: 70,
      duration_hours: 4,
      trade_frequency_minutes: 5,
      volume_target: 1000,
    },
    validationSchema,
    onSubmit: async (values, { setSubmitting, resetForm }) => {
      try {
        setSubmitStatus({ loading: true, error: null, success: false });

        await startMarketMaker(values);

        setSubmitStatus({
          loading: false,
          error: null,
          success: true,
        });

        // Reset form after successful submission
        resetForm();
        setMarkets([]);

        // Call onSuccess callback to return to list view
        if (onSuccess) {
          setTimeout(() => {
            onSuccess();
          }, 1500); // Show success message briefly before navigating back
        } else {
          // Clear success message after 5 seconds if no callback
          setTimeout(() => {
            setSubmitStatus((prev) => ({ ...prev, success: false }));
          }, 5000);
        }
      } catch (error) {
        console.error("Error starting market maker:", error);
        setSubmitStatus({
          loading: false,
          error:
            error.response?.data?.message ||
            error.message ||
            "Failed to start market maker",
          success: false,
        });
      } finally {
        setSubmitting(false);
      }
    },
  });

  // Load events on component mount
  useEffect(() => {
    const loadEvents = async () => {
      try {
        setLoadingEvents(true);
        const response = await fetchEvents();

        if (response && response.success && response.events) {
          setEvents(response.events);
        } else if (response && response.data) {
          setEvents(response.data);
        } else {
          setEvents([]);
        }
      } catch (error) {
        console.error("Error fetching events:", error);
        setEvents([]);
      } finally {
        setLoadingEvents(false);
      }
    };

    loadEvents();
  }, []);

  // Load markets immediately if event_id is provided in URL
  useEffect(() => {
    if (eventIdFromQuery) {
      const loadEventWithMarkets = async () => {
        try {
          setLoadingMarkets(true);
          const response = await fetchEventById(eventIdFromQuery);

          if (
            response &&
            response.success &&
            response.event &&
            response.event.sub_markets
          ) {
            setMarkets(response.event.sub_markets);
          } else {
            setMarkets([]);
          }
        } catch (error) {
          console.error("Error fetching event with markets:", error);
          setMarkets([]);
        } finally {
          setLoadingMarkets(false);
        }
      };

      loadEventWithMarkets();
    }
  }, [eventIdFromQuery]);

  // Load markets when event is selected
  useEffect(() => {
    const loadEventWithMarkets = async () => {
      if (!formik.values.event_id) {
        setMarkets([]);
        return;
      }

      try {
        setLoadingMarkets(true);
        const response = await fetchEventById(formik.values.event_id);

        if (
          response &&
          response.success &&
          response.event &&
          response.event.sub_markets
        ) {
          setMarkets(response.event.sub_markets);
        } else {
          setMarkets([]);
        }

        // Reset market selection when event changes
        formik.setFieldValue("market_id", "");
      } catch (error) {
        console.error("Error fetching event with markets:", error);
        setMarkets([]);
      } finally {
        setLoadingMarkets(false);
      }
    };

    loadEventWithMarkets();
  }, [formik.values.event_id]);

  return (
    <Card>
      <CardHeader>
        <h4 className="card-title mb-0">Market Maker Configuration</h4>
      </CardHeader>
      <CardBody>
        <p className="text-muted mb-4">
          Configure and start automated market making for existing events and
          markets.
        </p>

        <Form onSubmit={formik.handleSubmit}>
          {submitStatus.success && (
            <Alert color="success" className="mb-4">
              Market maker started successfully!
            </Alert>
          )}

          {submitStatus.error && (
            <Alert color="danger" className="mb-4">
              {submitStatus.error}
            </Alert>
          )}

          <Row>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="event_id">Select Event *</Label>
                <Input
                  type="select"
                  id="event_id"
                  name="event_id"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.event_id}
                  invalid={formik.touched.event_id && formik.errors.event_id}
                  disabled={loadingEvents}
                >
                  <option value="">
                    {loadingEvents ? "Loading events..." : "Select an event"}
                  </option>
                  {events.map((event) => (
                    <option key={event._id} value={event._id}>
                      {event.event_title || event.title}
                    </option>
                  ))}
                </Input>
                {formik.touched.event_id && formik.errors.event_id && (
                  <div className="text-danger mt-1">
                    {formik.errors.event_id}
                  </div>
                )}
              </div>
            </Col>

            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="market_id">Select Market</Label>
                <Input
                  type="select"
                  id="market_id"
                  name="market_id"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.market_id}
                  invalid={formik.touched.market_id && formik.errors.market_id}
                  disabled={!formik.values.event_id || loadingMarkets}
                >
                  <option value="">
                    {!formik.values.event_id
                      ? "Select an event first"
                      : loadingMarkets
                      ? "Loading markets..."
                      : markets.length === 0
                      ? "No markets available"
                      : "Select a market"}
                  </option>
                  {markets.map((market) => (
                    <option key={market._id} value={market._id}>
                      {market.name}
                    </option>
                  ))}
                </Input>
                {formik.touched.market_id && formik.errors.market_id && (
                  <div className="text-danger mt-1">
                    {formik.errors.market_id}
                  </div>
                )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="price_range_start">
                  Price Range Start (1-99) *
                </Label>
                <Input
                  type="number"
                  id="price_range_start"
                  name="price_range_start"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.price_range_start}
                  invalid={
                    formik.touched.price_range_start &&
                    formik.errors.price_range_start
                  }
                  min="1"
                  max="99"
                />
                {formik.touched.price_range_start &&
                  formik.errors.price_range_start && (
                    <div className="text-danger mt-1">
                      {formik.errors.price_range_start}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg="6">
              <div className="mb-3">
                <Label htmlFor="price_range_end">
                  Price Range End (2-100) *
                </Label>
                <Input
                  type="number"
                  id="price_range_end"
                  name="price_range_end"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.price_range_end}
                  invalid={
                    formik.touched.price_range_end &&
                    formik.errors.price_range_end
                  }
                  min="2"
                  max="100"
                />
                {formik.touched.price_range_end &&
                  formik.errors.price_range_end && (
                    <div className="text-danger mt-1">
                      {formik.errors.price_range_end}
                    </div>
                  )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col lg="4">
              <div className="mb-3">
                <Label htmlFor="duration_hours">Duration (Hours) *</Label>
                <Input
                  type="number"
                  id="duration_hours"
                  name="duration_hours"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.duration_hours}
                  invalid={
                    formik.touched.duration_hours &&
                    formik.errors.duration_hours
                  }
                  min="1"
                />
                {formik.touched.duration_hours &&
                  formik.errors.duration_hours && (
                    <div className="text-danger mt-1">
                      {formik.errors.duration_hours}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg="4">
              <div className="mb-3">
                <Label htmlFor="trade_frequency_minutes">
                  Trade Frequency (Minutes) *
                </Label>
                <Input
                  type="number"
                  id="trade_frequency_minutes"
                  name="trade_frequency_minutes"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.trade_frequency_minutes}
                  invalid={
                    formik.touched.trade_frequency_minutes &&
                    formik.errors.trade_frequency_minutes
                  }
                  min="1"
                />
                {formik.touched.trade_frequency_minutes &&
                  formik.errors.trade_frequency_minutes && (
                    <div className="text-danger mt-1">
                      {formik.errors.trade_frequency_minutes}
                    </div>
                  )}
              </div>
            </Col>

            <Col lg="4">
              <div className="mb-3">
                <Label htmlFor="volume_target">Volume Target *</Label>
                <Input
                  type="number"
                  id="volume_target"
                  name="volume_target"
                  onChange={formik.handleChange}
                  onBlur={formik.handleBlur}
                  value={formik.values.volume_target}
                  invalid={
                    formik.touched.volume_target && formik.errors.volume_target
                  }
                  min="1"
                />
                {formik.touched.volume_target &&
                  formik.errors.volume_target && (
                    <div className="text-danger mt-1">
                      {formik.errors.volume_target}
                    </div>
                  )}
              </div>
            </Col>
          </Row>

          <Row>
            <Col>
              <div className="text-end">
                <Button
                  type="submit"
                  color="primary"
                  disabled={
                    submitStatus.loading ||
                    loadingEvents ||
                    loadingMarkets ||
                    !formik.isValid ||
                    !formik.values.event_id
                  }
                >
                  {submitStatus.loading ? (
                    <>
                      <Spinner size="sm" className="me-2" />
                      Starting Market Maker...
                    </>
                  ) : (
                    "Start Market Maker"
                  )}
                </Button>
              </div>
            </Col>
          </Row>
        </Form>
      </CardBody>
    </Card>
  );
};

export default MarketMakerForm;
